import { useMemo } from "react";
import {
  MessageSquare,
  RefreshCw,
  Zap,
  Tag,
  UserCog,
  Eye,
  EyeOff,
  StickyNote,
  Bot,
  Sparkles,
  CalendarClock,
  Inbox,
  UserCheck,
  TrendingUp,
} from "lucide-react";

// Sentinel actor id for system-raised activity (e.g. an auto-created
// ticket's absence-based reassignment, or a consolidated alert firing —
// see csr-ticket-routing's raise_at_risk_tickets-style precedent, which
// already uses this same "system" sentinel for created_by_user_id).
const SYSTEM_ACTOR_ID = "system";
import { format, formatDistanceToNow } from "date-fns";
import { useAuth } from "../../../auth/hooks";
import type * as API from "@shared-core/data/api-types";

function activityIcon(
  activityType: API.TicketActivity["activity_type"],
  userId?: string | null,
) {
  if (userId === SYSTEM_ACTOR_ID) return Bot;
  switch (activityType) {
    case "comment":
      return MessageSquare;
    case "created":
      return Sparkles;
    case "status_changed":
      return RefreshCw;
    case "priority_changed":
      return Zap;
    case "category_changed":
    case "tagged":
    case "untagged":
      return Tag;
    case "reassigned":
    case "covering_assigned":
      return UserCog;
    case "watcher_added":
      return Eye;
    case "watcher_removed":
      return EyeOff;
    case "due_date_changed":
      return CalendarClock;
    case "department_assigned":
      return Inbox;
    case "claimed":
      return UserCheck;
    case "escalated":
      return TrendingUp;
  }
}

function formatDueDate(value: string | null | undefined): string {
  if (!value) return "no due date";
  return format(new Date(value), "d MMM yyyy, HH:mm");
}

function activityDescription(
  activity: API.TicketActivity,
  resolveUserName: (userId: string) => string,
): string {
  switch (activity.activity_type) {
    case "comment":
      return "commented";
    case "created":
      return "created this ticket";
    case "status_changed":
      return `changed status from ${activity.old_value} to ${activity.new_value}`;
    case "priority_changed":
      return `changed priority from ${activity.old_value} to ${activity.new_value}`;
    case "category_changed":
      return "changed the category";
    case "reassigned":
      return `reassigned this ticket to ${activity.new_value ? resolveUserName(activity.new_value) : "someone"}`;
    case "covering_assigned":
      return activity.new_value
        ? `assigned ${resolveUserName(activity.new_value)} to cover this ticket`
        : "cleared the covering assignment";
    case "watcher_added":
      return "added a watcher";
    case "watcher_removed":
      return "removed a watcher";
    case "tagged":
      return `added tag "${activity.new_value}"`;
    case "untagged":
      return `removed tag "${activity.old_value}"`;
    case "due_date_changed":
      return `changed due date from ${formatDueDate(activity.old_value)} to ${formatDueDate(activity.new_value)}`;
    case "department_assigned":
      return "routed this ticket to a department";
    case "claimed":
      return "claimed this ticket";
    case "escalated":
      return `escalated this ticket to ${activity.new_value ? resolveUserName(activity.new_value) : "the department lead"}`;
  }
}

type FeedEntry =
  | {
      kind: "activity";
      id: string;
      created_at: string;
      activity: API.TicketActivity;
    }
  | {
      kind: "note";
      id: string;
      created_at: string;
      note: API.Note | API.NoteInTicketView;
    };

interface TicketActivityTimelineProps {
  activity: API.TicketActivity[];
  /** Legacy per-ticket notes (wf_note, subject_type="task"). Folded into
   * this single read-only feed alongside real activity entries — this is
   * the full history log; commenting itself happens in the Comments
   * section on the Details tab, not here. */
  notes?: (API.Note | API.NoteInTicketView)[];
  resolveUserName: (userId: string) => string;
}

export function TicketActivityTimeline({
  activity,
  notes = [],
  resolveUserName,
}: TicketActivityTimelineProps) {
  const { user } = useAuth();

  const feed = useMemo<FeedEntry[]>(() => {
    const entries: FeedEntry[] = [
      ...activity.map((a): FeedEntry => ({
        kind: "activity",
        id: a.id,
        created_at: a.created_at,
        activity: a,
      })),
      ...notes.map((n): FeedEntry => ({
        kind: "note",
        id: n.id,
        created_at: n.created_at,
        note: n,
      })),
    ];
    return entries.sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [activity, notes]);

  const actorName = (userId: string | null | undefined) => {
    if (!userId || userId === SYSTEM_ACTOR_ID) return "System";
    return userId === user?.id ? "You" : resolveUserName(userId);
  };

  return (
    <div className="space-y-3">
      {feed.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        feed.map((entry) => {
          if (entry.kind === "note") {
            const { note } = entry;
            return (
              <div key={`note-${entry.id}`} className="flex gap-3">
                <div className="mt-0.5 shrink-0 flex size-6 items-center justify-center rounded-full bg-muted">
                  <StickyNote className="size-3.5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <span className="font-medium">
                      {actorName(note.user_id)}
                    </span>{" "}
                    <span className="text-muted-foreground">logged a note</span>
                  </div>
                  {note.note_title && (
                    <p className="text-sm mt-1 font-medium">
                      {note.note_title}
                    </p>
                  )}
                  <p className="text-sm mt-1 whitespace-pre-wrap">
                    {note.note_text}
                  </p>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {formatDistanceToNow(new Date(entry.created_at), {
                      addSuffix: true,
                    })}
                  </div>
                </div>
              </div>
            );
          }

          const { activity: act } = entry;
          const Icon = activityIcon(act.activity_type, act.user_id);
          return (
            <div key={`activity-${entry.id}`} className="flex gap-3">
              <div className="mt-0.5 shrink-0 flex size-6 items-center justify-center rounded-full bg-muted">
                <Icon className="size-3.5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-medium">{actorName(act.user_id)}</span>{" "}
                  <span className="text-muted-foreground">
                    {activityDescription(act, resolveUserName)}
                  </span>
                </div>
                {act.activity_type === "comment" && act.body && (
                  <p className="text-sm mt-1 whitespace-pre-wrap">{act.body}</p>
                )}
                {act.activity_type !== "comment" && act.body && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {act.body}
                  </p>
                )}
                <div className="text-xs text-muted-foreground mt-0.5">
                  {formatDistanceToNow(new Date(entry.created_at), {
                    addSuffix: true,
                  })}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
